"use client";

import React from "react";
import { MapPin, Mail, Phone, Facebook, Instagram, Linkedin, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function NeemContactOp() {
  return <section data-section-id="26719" id="contact" className="bg-background text-foreground py-20 md:py-32">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {}
          <div className="space-y-10">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground tracking-tight">
                Get in Touch
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-md">
                Ready to power up your venue or have a question about our rental service? Reach out and our team will get back to you within 24 hours.
              </p>
            </div>

            <div className="space-y-8">
              <div className="space-y-1">
                <h3 className="text-foreground font-bold text-lg">Our Office</h3>
                <div className="flex items-start gap-3 text-muted-foreground">
                  <MapPin className="w-5 h-5 mt-1 shrink-0 text-primary" />
                  <p>Strawinskylaan 3051, 1077 ZX Amsterdam, Netherlands</p>
                </div>
              </div>

              <div className="space-y-1">
                <h3 className="text-foreground font-bold text-lg">Support Email</h3>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="w-5 h-5 shrink-0 text-primary" />
                  <Link to="mailto:support@zapgo.nl" className="hover:text-primary transition-colors">
                    support@zapgo.nl
                  </Link>
                </div>
              </div>

              <div className="space-y-1">
                <h3 className="text-foreground font-bold text-lg">Partner Hotline</h3>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Phone className="w-5 h-5 shrink-0 text-primary" />
                  <Link to="tel:+31201234567" className="hover:text-primary transition-colors">
                    +31 (0) 20 123 4567
                  </Link>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <div className="flex gap-4">
                <Link to="https://instagram.com" newTab className="bg-primary text-primary-foreground p-3 rounded-full hover:opacity-90 transition-all hover:scale-110 flex items-center justify-center shadow-lg shadow-primary/20" aria-label="Instagram">
                  <Instagram className="w-5 h-5" />
                </Link>
                <Link to="https://facebook.com" newTab className="bg-secondary text-secondary-foreground p-3 rounded-full hover:opacity-90 transition-all hover:scale-110 flex items-center justify-center shadow-lg shadow-secondary/20" aria-label="Facebook">
                  <Facebook className="w-5 h-5" />
                </Link>
                <Link to="https://linkedin.com" newTab className="bg-primary text-primary-foreground p-3 rounded-full hover:opacity-90 transition-all hover:scale-110 flex items-center justify-center shadow-lg shadow-primary/20" aria-label="LinkedIn">
                  <Linkedin className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="bg-card p-8 md:p-10 rounded-3xl shadow-xl border border-border">
            <FormProvider formKey="contact_form_partners" formId="contact_form_partners" sectionName="contact-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: false
            },
            subject: {
              required: true
            },
            message: {
              required: true
            }
          }} submitText="Versturen" successMessage="Bedankt voor uw bericht! We nemen zo snel mogelijk contact met u op." errorMessage="Er is iets misgegaan. Probeer het later opnieuw." className="w-full">
              <ContactFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-accent/10 border border-primary/20 text-center rounded-2xl">
        <h3 className="text-xl font-bold text-foreground mb-2">Bedankt!</h3>
        <p className="text-muted-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Naam is verplicht"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Naam</FormLabel>
              <FormControl>
                <Input placeholder="Uw volledige naam" {...field} className="border-t-0 border-x-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary bg-transparent shadow-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-mailadres is verplicht",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ongeldig e-mailadres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">E-mailadres</FormLabel>
              <FormControl>
                <Input type="email" placeholder="naam@voorbeeld.nl" {...field} className="border-t-0 border-x-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary bg-transparent shadow-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Telefoonnummer</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+31 6 1234 5678" {...field} className="border-t-0 border-x-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary bg-transparent shadow-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" rules={{
        required: "Onderwerp is verplicht"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Onderwerp</FormLabel>
              <FormControl>
                <Input placeholder="Bijv: Partner worden" {...field} className="border-t-0 border-x-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary bg-transparent shadow-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Bericht is verplicht"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Bericht</FormLabel>
            <FormControl>
              <Textarea placeholder="Hoe kunnen we u helpen?" {...field} className="min-h-[120px] border-t-0 border-x-0 border-b border-input rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary bg-transparent shadow-none resize-y" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 text-destructive text-sm border border-destructive/20 rounded-lg">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[160px] bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 px-8 text-base font-bold cursor-pointer shadow-lg shadow-primary/20 transition-all active:scale-95">
          {isSubmitting ? <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Versturen...
            </> : "Versturen"}
        </Button>
      </div>
    </Form>;
}